<template>
  <div class="form-group mb-4">
    <label v-if="field.label" class="label mb-2"><span v-html="processedLabel"></span> <span v-if="field.required" class="text-danger">*</span></label>
    <component 
      :is="resolveComponent" 
      :field="field"
      :modelValue="props.modelValue"
      @update:modelValue="emit('update:modelValue', $event)"
      @option-selected="emit('option-selected', $event)"
      :maxDate="field.type === 'date' && field.maxDate ? field.maxDate : null"
    />
    <!-- TODO dinamicizzare maxDate -->
    <div v-if="showError" class="text-danger mt-1">{{ errorMessage }}</div>
    <div v-if="field.description && !showError" class="form-text mt-2" style="max-width: 600px;" v-html="processedDescription"></div>
  </div>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, PropType } from 'vue';
import { t } from '@/utils/i18nHelper';

// Define the structure of the field prop for better type safety
interface FormField {
  name: string;
  type: string;
  label?: string;
  labelKey?: string;
  placeholder?: string;
  description?: string;
  descriptionKey?: string;
  required?: boolean;
  maxDate?: Date;
}

const props = defineProps({
  field: {
    type: Object as PropType<FormField>,
    required: true,
  },
  modelValue: {
    type: [String, Number, Boolean, Date, Object] as PropType<any>,
    default: null,
  },
  showError: {
    type: Boolean,
    default: false,
  },
  errorMessage: {
    type: String,
    default: 'Questo campo è obbligatorio',
  }
});

const emit = defineEmits(['update:modelValue', 'option-selected']);

const processedLabel = computed(() => {
  const currentYear = new Date().getFullYear();
  const params = { 'CURRENT_YEAR-1': currentYear - 1 };
  return t(props.field.labelKey || '', props.field.label || '', params);
});

const processedDescription = computed(() => {
  const currentYear = new Date().getFullYear();
  const params = { 'CURRENT_YEAR-1': currentYear - 1 };
  return t(props.field.descriptionKey || '', props.field.description || '', params);
});

// Map field types from JSON to our Vue components
const componentMap: Record<string, any> = {
  date: defineAsyncComponent(() => import('./DatePicker.vue')),
  select: defineAsyncComponent(() => import('./SelectInput.vue')),
  advanced_select: defineAsyncComponent(() => import('./SearchableSelect.vue')),
  switch: defineAsyncComponent(() => import('./SwitchInput.vue')),
  currency: defineAsyncComponent(() => import('./CurrencyInput.vue')),
  text: defineAsyncComponent(() => import('./TextInput.vue')),
};

const resolveComponent = computed(() => {
  const componentName = props.field.type;
  if (componentName in componentMap) {
    return componentMap[componentName];
  }
  return componentMap.text; 
});
</script>

<style scoped>
.label {
  font-weight: 600;
}
.text-danger {
  font-size: 0.875em;
  color: #dc3545; /* Bootstrap's default danger color */
}
</style>